import React, { useRef } from 'react'
import { CopyValue } from './copy-value'
import { useCopy } from '../clipboard/clipboard'

// The DNS TXT record that proves a domain, laid out like Microsoft 365's
// setup table: one row per field, each with its own Copy, in the words every
// page that shows it uses (the organization's Domains, staff's organization
// SSO tab and the staff domains list), so they never drift apart.
//
// Field names differ between DNS hosts (Cloudflare Name / Content, Namecheap
// Host / Value, Route 53 Record name / Value, Google Cloud DNS DNS name). When
// auth names the domain's host (its `dnsHost`, from the domain's NS records
// and that host's own documentation), the rows use that host's labels, put
// the name in the form its name field wants first, and link to its own
// help. Otherwise -- many schools run their own DNS -- the generic rows name
// the common alternatives.

export const TXT_SHORT_NAME = '_edxr-verify'

// The record's TTL: an hour, like Microsoft 365's own table ("TTL 3600, or
// your provider default"). Any TTL works; this is what to type when asked.
export const TXT_TTL = '3600'
export const TXT_TTL_HINT = "1 hour, or your DNS host's default"

// Under the record: auth's domain-reverify job checks every domain's record
// daily, and a verified domain whose record is missing goes at risk.
export const TXT_KEEP_HINT = 'Keep this record in place: EducationXR re-checks it every day, and a domain whose record disappears goes at risk.'

export const GENERIC_NAME_LABEL = 'Name (sometimes called Host)'
export const GENERIC_VALUE_LABEL = 'Value (sometimes called Content or TXT data)'

// A value copied from inside a sentence: the text and a small Copy beside it.
const CopyInline = ({ value, label }) => {
  const copy = useCopy()
  const text = useRef(null)
  return (
    <span className="sw-copy-inline">
      <code ref={text}>{value}</code>
      <button type="button" className="btn btn-sm sw-btn ms-1 py-0" aria-label={`copy ${label}`} onClick={() => copy.copy(value, text)}>
        {copy.copied ? 'Copied' : 'Copy'}
      </button>
      {copy.manual && <span className="sw-hint ms-1" role="status">{copy.hint}</span>}
    </span>
  )
}

// fullName: the record's full name (auth's txtName, else _edxr-verify.<domain>).
// relativeName: its name relative to the DNS zone it is in (auth's
// txtRelativeName): `_edxr-verify` for a domain that is its own zone,
// `_edxr-verify.students` for students.district.org in district.org's zone.
// Unknown for a subdomain claim, the full name comes first: the short form
// would land the record in the wrong place.
export const TxtRecordFields = ({ idPrefix, domain, token, fullName, relativeName, dnsHost }) => {
  const full = fullName || `${TXT_SHORT_NAME}.${domain}`
  const labels = String(domain || '').split('.').filter(Boolean).length
  const short = relativeName || (labels <= 2 ? TXT_SHORT_NAME : null)
  const zone = short && full.length > short.length ? full.slice(short.length + 1) : domain
  const host = dnsHost || null
  const appends = host ? host.appendsDomain : null
  const fullFirst = appends === false || !short

  const nameLabel = host && host.nameLabel ? host.nameLabel : GENERIC_NAME_LABEL
  const valueLabel = host && host.valueLabel ? host.valueLabel : GENERIC_VALUE_LABEL
  const ttlLabel = host && host.ttlLabel ? host.ttlLabel : 'TTL'
  const ttlHint = host && host.ttlDefault ? `1 hour, or ${host.name}'s default (${host.ttlDefault})` : TXT_TTL_HINT

  const nameHint = !short
    ? <React.Fragment>If your DNS host adds your domain for you, type only the part before it.</React.Fragment>
    : appends === true
      ? <React.Fragment>{host.name} adds {zone} for you.</React.Fragment>
      : fullFirst
        ? <React.Fragment>If your DNS host adds {zone} for you, use <CopyInline value={short} label="the short name" /> instead.</React.Fragment>
        : <React.Fragment>Some DNS hosts want the full name: <CopyInline value={full} label="the full name" />. Most add {zone} for you.</React.Fragment>

  return (
    <div className="d-grid gap-2" data-testid="txt-record">
      {host &&
        <p className="m-0" data-testid="dns-host">
          Your DNS is at <strong>{host.name}</strong>.{' '}
          <a href={host.helpURL} target="_blank" rel="noopener noreferrer">How to add a TXT record at {host.name}</a>
        </p>}
      {host && host.note && <p className="sw-hint m-0">{host.note}</p>}
      <CopyValue id={`${idPrefix}-type`} label="Type" value="TXT" />
      <CopyValue id={`${idPrefix}-name`} label={nameLabel} value={fullFirst ? full : short} hint={nameHint} />
      <CopyValue id={`${idPrefix}-value`} label={valueLabel} value={token || ''} />
      <CopyValue id={`${idPrefix}-ttl`} label={ttlLabel} value={TXT_TTL} hint={ttlHint} />
    </div>
  )
}
